import { FC, useMemo } from 'react'
import Head from 'next/head'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import styles from './EncyclopediaCategory.module.scss'
import { EncyclopediaCatProps as Props } from '../types'
import SubCategoryCard from 'components/encyclopedia/sub-category-card/SubCategoryCard'
import ArticleSection from 'features/encyclopedia/components/article-section/ArticleSection'
import Hero from 'features/encyclopedia/components/hero/Hero'
import EncyclopediaNav from 'features/encyclopedia/components/encyclopedia-nav/EncyclopediaNav'
import HighlightedArticle from 'components/encyclopedia/highlighted-article/HighlightedArticle'
import { TITLE_POSTFIX } from 'constants/seo'

const EncyclopediaCategory: FC<Props> = ({ allArticles, categoryContent, relatedArticles, subCategories }) => {
  // i18n
  const { t } = useTranslation(['encyclopedia'])

  // Next router
  const { asPath, locale } = useRouter()

  // Constants
  const highlightedArticle = useMemo(
    () => relatedArticles.find((article) => article.isHighlighted || article.isHighlightedLanding),
    [relatedArticles]
  )

  return (
    <>
      {/* SEO */}
      <Head>
        <title>
          {categoryContent?.seo?.seoTitle ?? categoryContent?.title} {TITLE_POSTFIX}
        </title>
        <meta property="og:title" content={categoryContent?.seo?.seoTitle ?? categoryContent?.title} key="title" />
        <meta name="description" content={categoryContent?.seo?.metaDescription} />
        <link rel="canonical" href={`https://www.boltenergie.be/${locale}${asPath}`} />
      </Head>

      {/* BREADCRUMBS + SEARCH */}
      <EncyclopediaNav articles={allArticles} />

      {/* HERO */}
      <Hero category={categoryContent} />

      {/* TOPICS */}
      {!!subCategories?.length && (
        <section className={classNames('container', styles['sub-categories'])}>
          <h2>{t('subCategories', 'Topics')}</h2>
          <div className={styles['categories-grid']}>
            {subCategories?.map((subCategory, idx) => (
              <SubCategoryCard subCategory={subCategory} key={`${subCategory.slug}-${idx}`} color={categoryContent.color} />
            ))}
          </div>
        </section>
      )}

      {/* HIGHLIGHTED */}
      {highlightedArticle && <HighlightedArticle article={highlightedArticle} />}

      {/* ARTICLES */}
      <ArticleSection
        randomize
        limit={4}
        articles={relatedArticles}
        className={styles.articles}
        title={t('articles', 'Artikels')}
        filter={(article) => article.isPopular}
      />
    </>
  )
}

export default EncyclopediaCategory
